/* eslint-disable no-nested-ternary */
import { useMutation, useQuery } from "@apollo/client";
import getPlatformFAQs from "components/landing/helpers/getPlatformFAQs";
import { BLACK_FRIDAY_PRICE_IDS } from "components/landing/helpers/getPlatformPrices";
import TestimonialsSection from "components/landing/sections/TestimonialsSection";
import NavbarLayout from "components/layouts/NavbarLayout";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import TwoPassRender from "components/shared/TwoPassRender";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "components/ui/Accordion";
import { RadioGroup, RadioGroupItem } from "components/ui/RadioGroup";
import classNames from "components/ui/classNames";
import getYoutubeEmbedUrl from "components/utils/getEmbedUrl";
import { useAmplitude } from "lib/amplitude/Amplitude";
import { preserveOnlyUtmParameters } from "lib/analytics/utm";
import { getBooleanFlag } from "lib/flagging/FeatureFlags";
import PremiumPricesQuery from "lib/graphql/queries/PremiumPricesQuery";
import type {
  IPremiumPricesQuery,
  IPremiumPricesQueryVariables,
} from "lib/graphql/queries/__generated__/PremiumPricesQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import Redirect from "lib/http/Redirect";
import BillingPortalUrlMutation from "lib/queries/BillingPortalUrlMutation";
import CheckoutUrlMutation from "lib/queries/CheckoutUrlMutation";
import type { IBillingPortalUrlMutation } from "lib/queries/__generated__/BillingPortalUrlMutation.generated";
import type {
  ICheckoutUrlMutation,
  ICheckoutUrlMutationVariables,
} from "lib/queries/__generated__/CheckoutUrlMutation.generated";
import { CheckCircle2Icon, CircleAlertIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useAsyncCallback } from "react-async-hook";
import { FormattedMessage, useIntl } from "react-intl";
import { ICheckoutProduct, IStripeRecurringPriceInterval } from "types/graphqlTypes";

function Premium() {
  const intl = useIntl();
  const user = useSelfie();

  const [getBillingPortalUrl] = useMutation<IBillingPortalUrlMutation>(BillingPortalUrlMutation);
  const [checkoutUrl] = useMutation<ICheckoutUrlMutation, ICheckoutUrlMutationVariables>(CheckoutUrlMutation, {});
  const checkoutProduct = useAsyncCallback(async (product: ICheckoutProduct) => {
    if (!user) {
      return;
    }

    try {
      const { data } = await checkoutUrl({
        variables: {
          product,
          coupon: null,
          parameters: null,
        },
      });

      if (data && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (err) {
      if (err && err instanceof Error && /You already have Premium access/i.test(err.message)) {
        const { data } = await getBillingPortalUrl({});
        if (data && data.billingPortalUrl) {
          window.location.href = data.billingPortalUrl;
        }
      }
    }
  });

  const { data, loading } = useQuery<IPremiumPricesQuery, IPremiumPricesQueryVariables>(PremiumPricesQuery, {
    fetchPolicy: "network-only",
    variables: {},
  });

  const prices = data?.premiumPrices || [];

  const hasYearlyBlackFridayPrice = prices.some((node) => {
    const isYearlyBlackFriday = BLACK_FRIDAY_PRICE_IDS.includes(node.id);
    return isYearlyBlackFriday;
  });
  const [planPeriod, setPlanPeriod] = useState<"MONTHLY" | "YEARLY">(hasYearlyBlackFridayPrice ? "YEARLY" : "MONTHLY");
  useEffect(() => {
    if (hasYearlyBlackFridayPrice) {
      setPlanPeriod("YEARLY");
    }
  }, [hasYearlyBlackFridayPrice]);

  if (loading) {
    return null;
  }
  if (prices.length === 0) {
    return <Redirect to="/" />;
  }
  if (user?.isPremium || user?.isBlack) {
    return <Redirect to="/" />;
  }

  const monthlyPrice = prices.find((price) => price.recurringInterval === IStripeRecurringPriceInterval.Month);
  const yearlyPrice = prices.find((price) => price.recurringInterval === IStripeRecurringPriceInterval.Year);
  if (!monthlyPrice || !yearlyPrice) {
    return <Redirect to="/" />;
  }

  const frequentlyAskedQuestions = getPlatformFAQs(intl);
  const isSubscribing =
    checkoutProduct.currentParams !== null && checkoutProduct.currentParams.length > 0 && checkoutProduct.loading;

  return (
    <NavbarLayout disableStickyNavbar={true}>
      <FundamenteiMetadata title={intl.formatMessage({ defaultMessage: "Seja Premium | Fundamentei", id: "LuOBb5" })} />

      <main>
        <section className="mx-auto flex max-w-7xl flex-col items-center gap-y-12 px-6 py-16 lg:px-0">
          <header className="mx-auto flex flex-col gap-4 text-center">
            <h3 className="inline-flex max-w-5xl text-3xl font-bold tracking-tight text-black sm:text-5xl">
              <FormattedMessage
                defaultMessage="Um único plano. O site mais completo com todos os dados que qualquer investidor precisa"
                id="WTm3pV"
              />
            </h3>
          </header>

          <RadioGroup
            loop={false}
            className={classNames(
              "grid w-full items-center justify-between rounded-2xl border border-slate-200 bg-white p-1",
              "flex max-w-sm justify-normal space-x-2 sm:w-auto",
            )}
            orientation="horizontal"
            value={planPeriod}
            onValueChange={(value) => setPlanPeriod(value as "MONTHLY" | "YEARLY")}
          >
            <RadioGroupItem
              value="MONTHLY"
              title={intl.formatMessage({ defaultMessage: "Mensal", id: "ie4Tde" })}
              className={classNames(
                "h-12 w-full items-center justify-center rounded-xl text-center sm:w-28",
                "border border-transparent text-sm font-medium tracking-wide text-slate-500",
                "data-[state=checked]:bg-violet-50 data-[state=checked]:font-bold data-[state=checked]:text-violet-600",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-100 focus-visible:ring-offset-1 focus-visible:ring-offset-gray-300",
                "data-[state=checked]:focus-visible:rounded-xl data-[state=checked]:focus-visible:bg-gray-50 data-[state=checked]:focus-visible:text-gray-600",
              )}
            >
              <FormattedMessage defaultMessage="Mensal" id="ie4Tde" />
            </RadioGroupItem>
            <RadioGroupItem
              value="YEARLY"
              title={intl.formatMessage({ defaultMessage: "Anual", id: "nck+Ad" })}
              className={classNames(
                "h-12 w-full items-center justify-center rounded-xl text-center sm:w-28",
                "border border-transparent text-sm font-medium tracking-wide text-slate-500",
                "data-[state=checked]:bg-violet-50 data-[state=checked]:font-bold data-[state=checked]:text-violet-600",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-100 focus-visible:ring-offset-1 focus-visible:ring-offset-gray-300",
                "data-[state=checked]:focus-visible:rounded-xl data-[state=checked]:focus-visible:bg-gray-50 data-[state=checked]:focus-visible:text-gray-600",
              )}
            >
              <FormattedMessage defaultMessage="Anual" id="nck+Ad" />
            </RadioGroupItem>
          </RadioGroup>

          <div className="mx-auto grid max-w-sm items-start gap-6 md:max-w-none md:grid-cols-2">
            <FreePlanCard />
            <PremiumPlanCard
              price={planPeriod === "MONTHLY" ? monthlyPrice : yearlyPrice}
              isSubscribing={isSubscribing}
              onClickSubscribe={checkoutProduct.execute}
              hasYearlyBlackFriday={BLACK_FRIDAY_PRICE_IDS.includes(yearlyPrice.id)}
            />
          </div>

          {/* FAQ */}
          <Accordion type="multiple" className="mx-auto w-full max-w-4xl">
            {frequentlyAskedQuestions.map((node) => (
              <AccordionItem key={node.question} value={node.question} className="border-slate-200">
                <AccordionTrigger
                  className={classNames(
                    "justify-between gap-2 rounded px-4 text-left text-base font-medium text-slate-800 lg:text-lg lg:tracking-wide",
                  )}
                >
                  {node.question}
                </AccordionTrigger>
                <AccordionContent className="rounded-b px-4 pb-3 text-slate-600">
                  <div className="space-y-4">
                    {node.answer.map((answer, index) => (
                      <p className="text-base lg:tracking-wide" key={`answer/${String(index)}`}>
                        {answer}
                      </p>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {intl.locale === "pt-BR" && (
          <section className="border-b border-slate-800 bg-slate-950 px-6 py-12 lg:px-0 lg:py-20">
            <header className="mx-auto max-w-5xl pb-16 text-center">
              <h3 className="text-pretty text-4xl font-medium tracking-tighter text-white sm:text-5xl">
                <FormattedMessage defaultMessage="Conheça nosso site e os diferenciais" id="XzqNKE" />
              </h3>
              <p className="mt-1 text-lg text-neutral-400">
                <FormattedMessage
                  defaultMessage="Quem assina, ama. Confira abaixo uma apresentação completa do site."
                  id="2prt6g"
                />
              </p>
            </header>
            <div className="mx-auto max-w-7xl">
              <div className="aspect-video w-full overflow-hidden rounded-3xl border border-slate-950 bg-slate-800 shadow-sm">
                <iframe
                  title={intl.formatMessage({ defaultMessage: "Conheça o Fundamentei", id: "ISuEVu" })}
                  className="h-full w-full rounded-4xl"
                  allowFullScreen={true}
                  allow="autoplay"
                  src={getYoutubeEmbedUrl("oqytcHRwGro", {
                    videoUrl: "https://www.youtube.com/watch?v=oqytcHRwGro",
                  })}
                />
              </div>
            </div>
          </section>
        )}

        <TestimonialsSection />
      </main>
    </NavbarLayout>
  );
}

function FreePlanCard() {
  const intl = useIntl();
  const selfie = useSelfie();
  const router = useRouter();
  return (
    <div className={classNames("relative flex h-full flex-col rounded-2xl bg-neutral-100 p-6")}>
      <div className={classNames("mb-4 gap-1 text-zinc-900")}>
        <p className="text-2xl font-black tracking-tight">
          <FormattedMessage defaultMessage="Gratuito" id="AMJRek" />
        </p>
        <p className="text-zinc-500">
          <FormattedMessage defaultMessage="Pra quem se vira com pouquíssimos dados..." id="yX23NH" />
        </p>
      </div>

      <div className="grow space-y-4">
        <ul className="list-none space-y-3 text-sm">
          {[
            intl.formatMessage({
              defaultMessage: "Sem acesso ao ranking",
              id: "bfob0w",
            }),
            intl.formatMessage({
              defaultMessage: "Sem acesso às discussões",
              id: "EVZrcT",
            }),
            intl.formatMessage({
              defaultMessage: "Somente o último balanço anual",
              id: "KwpU4l",
            }),
            intl.formatMessage({
              defaultMessage: "Valuation diário",
              id: "NLF0Zv",
            }),
            intl.formatMessage({
              defaultMessage: "Gráficos e histórico de dados limitado",
              id: "NGAdgp",
            }),
            intl.formatMessage({
              defaultMessage: "Gerenciador de ativos limitado",
              id: "voxqoc",
            }),
            intl.formatMessage({
              defaultMessage: "Sem acesso ao Screener (planilhão)",
              id: "01kV6A",
            }),
          ].map((feature) => (
            <li key={feature} className="flex items-center gap-2">
              <CircleAlertIcon
                strokeWidth={2}
                className="h-5 w-5 fill-rose-50/50 text-rose-600 dark:fill-transparent"
              />
              <span className="font-medium text-slate-600">{feature}</span>
            </li>
          ))}
        </ul>

        {selfie ? (
          <button
            type="button"
            disabled={true}
            className={classNames(
              "inline-flex w-full items-center justify-center rounded-xl",
              "px-4 py-3 text-sm font-medium",
              "bg-neutral-200 text-slate-700",
            )}
          >
            <FormattedMessage defaultMessage="Seu plano atual" id="jsAAEQ" />
          </button>
        ) : (
          <Link
            href={{
              pathname: "/sign-up",
              query: preserveOnlyUtmParameters(router.query),
            }}
            className={classNames(
              "inline-flex w-full items-center justify-center rounded-xl",
              "px-4 py-3 text-sm font-medium shadow",
              "bg-white text-slate-700 hover:bg-emerald-700 hover:text-white hover:opacity-95",
            )}
          >
            <FormattedMessage defaultMessage="Cadastre-se" id="Fhi5N0" />
          </Link>
        )}
      </div>
    </div>
  );
}

interface PremiumPlanCardProps {
  price: IPremiumPricesQuery["premiumPrices"][0];
  isSubscribing?: boolean;
  hasYearlyBlackFriday?: boolean;
  onClickSubscribe: (product: ICheckoutProduct) => void;
}
function PremiumPlanCard({ price, isSubscribing, onClickSubscribe, hasYearlyBlackFriday }: PremiumPlanCardProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const router = useRouter();
  const amplitude = useAmplitude();
  const isMonthly = price.recurringInterval === IStripeRecurringPriceInterval.Month;
  const isYearly = price.recurringInterval === IStripeRecurringPriceInterval.Year;
  const isYearlyBlackFriday = isYearly && BLACK_FRIDAY_PRICE_IDS.includes(price.id);
  return (
    <div className="relative h-full">
      {isMonthly && !hasYearlyBlackFriday && (
        <span className="absolute right-6 top-6 rounded-2xl bg-orange-600/10 p-1.5 px-3 text-xs font-bold uppercase text-orange-600">
          <FormattedMessage defaultMessage="2 meses grátis no anual" id="ZmFpZQ" />
        </span>
      )}
      {isMonthly && hasYearlyBlackFriday && (
        <span className="absolute right-6 top-6 rounded-2xl bg-slate-950 p-1.5 px-3 text-xs font-bold uppercase text-white">
          <FormattedMessage defaultMessage="Black Friday no anual (45% OFF)" id="iWSlxi" />
        </span>
      )}
      {isYearly && !isYearlyBlackFriday && (
        <span className="absolute right-6 top-6 rounded-2xl bg-purple-600/10 p-1.5 px-3 text-xs font-bold uppercase text-purple-600">
          <FormattedMessage defaultMessage="2 meses grátis" id="vxKW7J" />
        </span>
      )}
      {isYearly && isYearlyBlackFriday && (
        <span className="absolute right-6 top-6 z-10 rounded-2xl bg-lime-200/10 p-1.5 px-3 text-xs font-bold uppercase text-lime-300">
          <FormattedMessage defaultMessage="Black Friday 45% OFF!" id="Om64Oi" />
        </span>
      )}
      <div
        className={classNames("relative flex h-full flex-col rounded-2xl border-transparent bg-brand/5 p-6", {
          "border-transparent bg-slate-800": isYearlyBlackFriday,
        })}
      >
        <div
          className={classNames("mb-4 gap-1 text-brand", {
            "text-white": isYearlyBlackFriday,
          })}
        >
          <p className="text-2xl font-black tracking-tight">
            <FormattedMessage defaultMessage="Premium" id="C5xzTC" />
          </p>
          <p className="mb-0.5 inline-flex items-baseline">
            <span className="text-3xl font-bold">{price.formattedAmount}</span>

            {isMonthly && (
              <span className="font-medium text-zinc-500">
                /<FormattedMessage defaultMessage="mês" id="6Zs4WI" />
              </span>
            )}
            {isYearly && (
              <span
                className={classNames("font-medium text-zinc-500", {
                  "text-zinc-200": isYearlyBlackFriday,
                })}
              >
                /<FormattedMessage defaultMessage="ano" id="/rC3CD" />
              </span>
            )}
          </p>
          <p
            className={classNames("text-zinc-500", {
              "text-zinc-400": isYearlyBlackFriday,
            })}
          >
            <FormattedMessage defaultMessage="Para quem quer realmente evoluir como investidor!" id="hOslGL" />
          </p>
        </div>

        <div className="grow space-y-4">
          <ul className="list-none space-y-3 text-sm">
            {[
              intl.formatMessage({
                defaultMessage: "Ranking de ativos",
                id: "mhPFmD",
              }),
              intl.formatMessage({
                defaultMessage: "Acesso as discussões",
                id: "9nAVS0",
              }),
              intl.formatMessage({
                defaultMessage: "Destaques trimestrais das empresas",
                id: "y12qOP",
              }),
              intl.formatMessage({
                defaultMessage: "Acesso completo ao Screener (planilhão)",
                id: "ZbJBml",
              }),
              intl.formatMessage({
                defaultMessage: "Balanços trimestrais atualizados",
                id: "k0OV3T",
              }),
              intl.formatMessage({
                defaultMessage: "Histórico completo de dados",
                id: "ZCBh6W",
              }),
            ].map((feature) => (
              <li key={feature} className="flex items-center gap-2">
                <CheckCircle2Icon
                  strokeWidth={2}
                  className={classNames("h-5 w-5 fill-emerald-50/50 text-emerald-600 dark:fill-transparent", {
                    "fill-lime-300/20 text-lime-500": isYearlyBlackFriday,
                  })}
                />
                <span
                  className={classNames("font-medium text-slate-600", {
                    "text-slate-300": isYearlyBlackFriday,
                  })}
                >
                  {feature}
                </span>
              </li>
            ))}
          </ul>

          {selfie ? (
            <button
              type="button"
              disabled={isSubscribing}
              onClick={() => {
                const product = isMonthly
                  ? ICheckoutProduct.MonthlyRecurringSubscription
                  : ICheckoutProduct.YearlyRecurringSubscription;
                amplitude.logEvent(`Clicked on Subscribe to Premium`, {
                  product,
                });
                onClickSubscribe(product);
              }}
              className={classNames(
                "inline-flex w-full items-center justify-center rounded-xl",
                "px-4 py-3 text-sm font-medium shadow",
                "bg-brand text-white hover:opacity-80",
                { "bg-lime-900/80 text-lime-300": isYearlyBlackFriday },
              )}
            >
              {isSubscribing ? (
                <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" />
              ) : selfie.viewerCanStartPremiumTrial ? (
                <FormattedMessage defaultMessage="Teste grátis por 7 dias" id="7OI0Qr" />
              ) : (
                <FormattedMessage defaultMessage="Assinar" id="BOV3/Y" />
              )}
            </button>
          ) : (
            <Link
              href={{
                pathname: "/sign-up",
                query: {
                  ...preserveOnlyUtmParameters(router.query),
                  checkout: isMonthly
                    ? ICheckoutProduct.MonthlyRecurringSubscription
                    : ICheckoutProduct.YearlyRecurringSubscription,
                },
              }}
              className={classNames(
                "inline-flex w-full items-center justify-center rounded-xl",
                "px-4 py-3 text-sm font-medium shadow",
                "bg-brand text-white hover:opacity-80",
                { "bg-lime-900/80 text-lime-300": isYearlyBlackFriday },
              )}
            >
              {getBooleanFlag("fundamentei/isFreeTrialAvailable") ? (
                <FormattedMessage defaultMessage="Teste grátis por 7 dias" id="7OI0Qr" />
              ) : (
                <FormattedMessage defaultMessage="Assinar" id="BOV3/Y" />
              )}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PremiumPage() {
  return <TwoPassRender>{() => <Premium />}</TwoPassRender>;
}
